import { createContext, type ReactNode, useContext, useMemo } from 'react';
import type { ApiClient, AuthStrategy } from '@/api/client';
import { createApiClient } from '@/api/client';
import { getIdToken, isCognitoConfigured } from '@/lib/amplify';
import { getRuntimeConfig } from '@/lib/runtime-config';
import { createMockClient } from '@/mocks/mockClient';
import { usePlayStore } from '@/store/play';

const useMock =
  import.meta.env.VITE_USE_MOCK_API === 'true' || import.meta.env.VITE_USE_MOCK_API === undefined;

/**
 * Backend API base URL. Three resolution paths in priority order:
 *   1. Runtime config (`/config.json`) — real-AWS prod sets
 *      `apiUrl: "https://api.quiz.massimo.click"` here.
 *   2. `${window.location.origin}/api` — Vite dev rides through the
 *      `/api` → cors-proxy → Floci path; same-origin keeps the
 *      HTTPS scheme carrying so phones on the LAN can use port 5173.
 *   3. `/api` — SSR / unit-test fallback. Shouldn't happen in
 *      practice; included so `resolveBackendUrl()` always returns a
 *      truthy string.
 */
function resolveBackendUrl(): string {
  const cfg = getRuntimeConfig();
  if (cfg.apiUrl && /^https?:\/\//.test(cfg.apiUrl)) return cfg.apiUrl;
  if (typeof window === 'undefined') return '/api';
  return `${window.location.origin}/api`;
}

interface ApiContextValue {
  client: ApiClient;
  setAuthStrategy: (a: AuthStrategy) => void;
}

// Auto-apply a dev identity when running against a real backend in Vite
// dev mode. The backend's `requireCognito` honors `x-user-sub` /
// `x-user-email` headers as a Cognito JWT replacement when there's no
// JWT present (and API Gateway strips these headers in prod).
const DEV_AUTH: AuthStrategy =
  !useMock && import.meta.env.DEV
    ? { kind: 'dev', sub: 'dev-host-1', email: 'dev@quiz.massimo.click' }
    : null;

let currentAuth: AuthStrategy = DEV_AUTH;

const ApiContext = createContext<ApiContextValue | null>(null);

/**
 * Auth strategy precedence on each request:
 *   1. Explicit per-call `auth` (kiosk token on control screens) — wins
 *      because the api client honors `auth` before calling getAuth().
 *   2. Player session token (X-Player-Token) — ONLY for requests under
 *      `/sessions/{stored-session-id}/...`. The same browser tab may
 *      hold a host-cognito identity and a joined-player token at the
 *      same time (host plays along), so we route per-path: anything
 *      outside the joined session falls through to host auth.
 *   3. Cognito ID token (Authorization: Bearer …) — fetched on demand
 *      from the Amplify session when VITE_COGNITO_USER_POOL_ID is set.
 *   4. `currentAuth` set via setAuthStrategy (manual override).
 *   5. DEV_AUTH (header-based Cognito sub fallback) — used only when
 *      Cognito isn't configured (Floci / dev without a user pool).
 */
async function resolveAuth({ path }: { path: string }): Promise<AuthStrategy> {
  const { sessionToken, sessionId } = usePlayStore.getState();
  if (sessionToken && sessionId) {
    const playerPathPrefix = `/sessions/${sessionId}/`;
    if (path === `/sessions/${sessionId}` || path.startsWith(playerPathPrefix)) {
      return { kind: 'player-token', token: sessionToken };
    }
  }
  if (isCognitoConfigured()) {
    const token = await getIdToken();
    if (token) return { kind: 'bearer', token };
    // No Cognito session yet — fall through to currentAuth (rarely set)
    // and ultimately null. The guard around /host/* should have already
    // bounced the user to /host/auth before we get here.
  }
  return currentAuth ?? DEV_AUTH;
}

export function ApiProvider({ children }: { children: ReactNode }) {
  const value = useMemo<ApiContextValue>(() => {
    const client = useMock
      ? createMockClient()
      : createApiClient({
          baseUrl: resolveBackendUrl(),
          getAuth: resolveAuth,
        });
    return {
      client,
      setAuthStrategy: (a) => {
        currentAuth = a;
      },
    };
  }, []);

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiClient {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('useApi outside <ApiProvider>');
  return ctx.client;
}

export function useSetAuth() {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('useSetAuth outside <ApiProvider>');
  return ctx.setAuthStrategy;
}

export const IS_MOCK = useMock;
